/**
 * NotificationRowLayout
 *
 * Shared presentational layout for a notification row, ported from the common
 * `Row(avatar, Expanded(title + subtitle), trailing thumbnail)` skeleton that
 * every per-type widget under
 * `lib/presentation/screens/messages/widgets/notification_*_item.dart` repeats
 * (e.g. `notification_content_like_item.dart:76-152`). Modelled on the wallet
 * row `GiftTransactionItem.tsx`.
 *
 * Slots:
 *   - `leading`     — left node (a tappable avatar or a fixed icon). Tap on it
 *                     fires `onLeadingTap` (Flutter `onUserTap`) when provided.
 *   - `title`       — the row title string.
 *   - `subtitle`    — the row subtitle node (string or composed JSX, e.g. the
 *                     gift coins row).
 *   - `trailing`    — optional trailing media thumbnail node. Tap fires
 *                     `onTrailingTap` (Flutter `onItemMediaTap`).
 *   - whole-row tap — fires `onItemTap` (Flutter `onItemTap`).
 *
 * Each per-type item component computes its own strings/thumbnail and passes
 * them in — this keeps ~15 near-duplicate types behind one layout rather than
 * one large file per type (component-size rule).
 *
 * ## QA ids are intentionally NOT entity-suffixed
 *
 * The three tap targets carry the bare ids `notification-row-body`,
 * `notification-row-avatar` and `notification-row-media`. The `data-qa-*`
 * convention asks list rows to suffix the entity id, and that suffix already
 * exists one level up: the enclosing `NotificationItemContainer` renders
 * `data-qa-id="inbox-notification-<id>"` with
 * `data-qa-value="<AppNotificationType name>"` and
 * `data-qa-status="read" | "unread"` (`NotificationItemContainer.tsx:47-59`).
 * That status token is the assertion target for read state — it is the only
 * non-visual signal of it, since the read/unread difference is otherwise just
 * the container background tint. The QA agent therefore locates a
 * specific row by that container id and finds the tap target as a DESCENDANT of
 * it, so duplicating the id here would only make the selectors longer without
 * making them more specific. Do not "fix" these to per-entity ids.
 */
import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface NotificationRowLayoutProps {
  leading: React.ReactNode;
  title: string;
  subtitle: React.ReactNode;
  trailing?: React.ReactNode;
  /** Whole-row tap (Flutter `onItemTap`). */
  onItemTap?: () => void;
  /** Tap on the leading avatar (Flutter `onUserTap`). When omitted, the leading node is not independently tappable. */
  onLeadingTap?: () => void;
  /** Tap on the trailing media thumbnail (Flutter `onItemMediaTap`). */
  onTrailingTap?: () => void;
}

export const NotificationRowLayout: React.FC<NotificationRowLayoutProps> = ({
  leading,
  title,
  subtitle,
  trailing,
  onItemTap,
  onLeadingTap,
  onTrailingTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const stopAnd = (handler?: () => void) => (e: React.MouseEvent) => {
    if (!handler) return;
    e.stopPropagation();
    handler();
  };

  return (
    <div
      {...qaAttr({ id: 'notification-row-body' })}
      onClick={onItemTap}
      style={{
        display: 'flex',
        alignItems: 'center',
        cursor: onItemTap ? 'pointer' : 'default',
      }}
    >
      {/* Leading avatar / icon */}
      <div
        {...qaAttr({ id: 'notification-row-avatar' })}
        onClick={stopAnd(onLeadingTap)}
        style={{ flexShrink: 0, cursor: onLeadingTap ? 'pointer' : 'inherit' }}
      >
        {leading}
      </div>

      {/* Title + subtitle */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          paddingLeft: responsive.spacing.sm,
          paddingRight: responsive.spacing.sm,
        }}
      >
        <div
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.medium,
          }}
        >
          {title}
        </div>
        <div
          style={{
            marginTop: responsive.spacing.xs,
            color: theme.text.secondary,
            fontSize: responsive.font.sm,
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* Trailing media thumbnail */}
      {trailing && (
        <div
          {...qaAttr({ id: 'notification-row-media' })}
          onClick={stopAnd(onTrailingTap)}
          style={{ flexShrink: 0, cursor: onTrailingTap ? 'pointer' : 'inherit' }}
        >
          {trailing}
        </div>
      )}
    </div>
  );
};
